import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, X, Upload, Star } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { Spinner } from '../components/Spinner';
import { InlineSpinner } from '../components/InlineSpinner';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import styles from './ColorPalettesPage.module.css';

interface PaletteImage {
  url: string;
  isDefault: boolean;
  _id?: string;
}

interface ColorPalette {
  _id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  images: PaletteImage[];
  coverImage: string;
  inStock: boolean;
  createdAt: string;
}

const initialFormState = {
  name: '',
  description: '',
  price: 0,
  inStock: true,
};

function getImageSrc(url: string) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
}

function getCoverImageSrc(palette: ColorPalette) {
  if (palette.images && palette.images.length > 0) {
    const defaultImg = palette.images.find((img) => img.isDefault) || palette.images[0];
    return getImageSrc(defaultImg.url);
  }
  return palette.imageUrl ? getImageSrc(palette.imageUrl) : '';
}

export function ColorPalettesPage() {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [palettes, setPalettes] = useState<ColorPalette[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPalette, setEditingPalette] = useState<ColorPalette | null>(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Image state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<PaletteImage[]>([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState<number>(0);

  const fetchPalettes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/color-palettes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setPalettes(data.palettes || []);
    } catch (error) {
      console.error('Error fetching color palettes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPalettes();
  }, [token]);

  const openCreateModal = () => {
    setEditingPalette(null);
    setFormData(initialFormState);
    setSelectedFiles([]);
    setExistingImages([]);
    setDefaultImageIndex(0);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (palette: ColorPalette) => {
    setEditingPalette(palette);
    setFormData({
      name: palette.name,
      description: palette.description,
      price: palette.price,
      inStock: palette.inStock,
    });

    const images = palette.images && palette.images.length > 0
      ? palette.images
      : palette.imageUrl
        ? [{ url: palette.imageUrl, isDefault: true }]
        : [];
    setExistingImages(images);
    setSelectedFiles([]);
    const defaultIdx = images.findIndex((img) => img.isDefault);
    setDefaultImageIndex(defaultIdx >= 0 ? defaultIdx : 0);
    setError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPalette(null);
    setFormData(initialFormState);
    setSelectedFiles([]);
    setExistingImages([]);
    setDefaultImageIndex(0);
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...newFiles]);
      if (existingImages.length === 0 && selectedFiles.length === 0) {
        setDefaultImageIndex(0);
      }
    }
    e.target.value = '';
  };

  const removeExistingImage = (idx: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));
    if (defaultImageIndex === idx) {
      setDefaultImageIndex(0);
    } else if (defaultImageIndex > idx) {
      setDefaultImageIndex((prev) => prev - 1);
    }
  };

  const removeNewFile = (idx: number) => {
    const combinedIdx = existingImages.length + idx;
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
    if (defaultImageIndex === combinedIdx) {
      setDefaultImageIndex(0);
    } else if (defaultImageIndex > combinedIdx) {
      setDefaultImageIndex((prev) => prev - 1);
    }
  };

  const totalImages = existingImages.length + selectedFiles.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (totalImages === 0) {
      setError('Please upload at least one image');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const url = editingPalette
        ? `${API_BASE_URL}/api/color-palettes/${editingPalette._id}`
        : `${API_BASE_URL}/api/color-palettes`;
      const method = editingPalette ? 'PUT' : 'POST';

      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('description', formData.description);
      fd.append('price', String(formData.price));
      fd.append('inStock', String(formData.inStock));

      if (editingPalette && existingImages.length > 0) {
        fd.append('existingImages', JSON.stringify(existingImages));
      }

      selectedFiles.forEach((file) => {
        fd.append('images', file);
      });

      fd.append('defaultImageIndex', String(defaultImageIndex));

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save color palette');
      }

      closeModal();
      fetchPalettes();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this color palette? This cannot be undone.',
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/color-palettes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete color palette');
      }

      await fetchPalettes();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Color Palettes</h1>
          <p>Manage your standalone paint color sets</p>
        </div>
        <button className={styles.addBtn} onClick={openCreateModal}>
          <Plus size={20} />
          Add Color Palette
        </button>
      </header>

      {loading ? (
        <Spinner label="Loading color palettes..." size="lg" />
      ) : palettes.length === 0 ? (
        <div className={styles.empty}>
          <p>No color palettes yet. Add your first product!</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {palettes.map((palette) => (
            <div key={palette._id} className={styles.card}>
              <div className={styles.cardImage}>
                <img src={getCoverImageSrc(palette)} alt={palette.name} />
                {!palette.inStock && <div className={styles.outOfStock}>Out of Stock</div>}
                {palette.images && palette.images.length > 1 && (
                  <div className={styles.imageCount}>{palette.images.length} images</div>
                )}
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardHeader}>
                  <h3>{palette.name}</h3>
                  <span className={styles.price}>EGP {palette.price}</span>
                </div>
                <p className={styles.description}>{palette.description}</p>
                <div className={styles.cardFooter}>
                  <span className={`${styles.stockStatus} ${palette.inStock ? styles.inStock : styles.noStock}`}>
                    {palette.inStock ? 'In Stock' : 'Out of Stock'}
                  </span>
                  <div className={styles.actions}>
                    <button
                      className={styles.editBtn}
                      onClick={() => openEditModal(palette)}
                      disabled={deletingId === palette._id}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(palette._id)}
                      disabled={deletingId === palette._id}
                    >
                      {deletingId === palette._id ? <InlineSpinner size={16} /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={closeModal}>
              <X size={20} />
            </button>
            <h2>{editingPalette ? 'Edit Color Palette' : 'Add Color Palette'}</h2>

            {error && <div className={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Price (EGP)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min={0}
                    step="0.01"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Stock Status</label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={formData.inStock}
                      onChange={handleChange}
                    />
                    In Stock
                  </label>
                </div>
              </div>

              {/* Image Upload Section */}
              <div className={styles.formGroup}>
                <label>Images {totalImages === 0 && <span style={{ color: 'var(--danger)' }}>*</span>}</label>
                <label className={styles.uploadArea}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <Upload size={20} />
                  <span>Click to add images</span>
                  <span className={styles.uploadHint}>JPEG, PNG, WebP (max 10MB each)</span>
                </label>

                {totalImages > 0 && (
                  <div className={styles.imagePreviews}>
                    {existingImages.map((img, idx) => (
                      <div
                        key={img.url}
                        className={`${styles.imagePreview} ${defaultImageIndex === idx ? styles.imagePreviewDefault : ''}`}
                      >
                        <img src={getImageSrc(img.url)} alt={`Image ${idx + 1}`} />
                        <div className={styles.imagePreviewActions}>
                          <button
                            type="button"
                            className={`${styles.coverBtn} ${defaultImageIndex === idx ? styles.coverBtnActive : ''}`}
                            onClick={() => setDefaultImageIndex(idx)}
                            title="Set as cover"
                          >
                            <Star size={14} />
                          </button>
                          <button
                            type="button"
                            className={styles.removeImgBtn}
                            onClick={() => removeExistingImage(idx)}
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        {defaultImageIndex === idx && (
                          <span className={styles.coverLabel}>Cover</span>
                        )}
                      </div>
                    ))}

                    {selectedFiles.map((file, idx) => {
                      const combinedIdx = existingImages.length + idx;
                      return (
                        <div
                          key={`new-${idx}`}
                          className={`${styles.imagePreview} ${defaultImageIndex === combinedIdx ? styles.imagePreviewDefault : ''}`}
                        >
                          <img src={URL.createObjectURL(file)} alt={file.name} />
                          <div className={styles.imagePreviewActions}>
                            <button
                              type="button"
                              className={`${styles.coverBtn} ${defaultImageIndex === combinedIdx ? styles.coverBtnActive : ''}`}
                              onClick={() => setDefaultImageIndex(combinedIdx)}
                              title="Set as cover"
                            >
                              <Star size={14} />
                            </button>
                            <button
                              type="button"
                              className={styles.removeImgBtn}
                              onClick={() => removeNewFile(idx)}
                              title="Remove"
                            >
                              <X size={14} />
                            </button>
                          </div>
                          {defaultImageIndex === combinedIdx && (
                            <span className={styles.coverLabel}>Cover</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button type="submit" className={styles.submitBtn} disabled={submitting}>
                {submitting ? 'Saving...' : editingPalette ? 'Update Color Palette' : 'Add Color Palette'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
